import { usesHomeImprovementAgreement } from '../../../common/helpers/esrContractCompany'
import { isCashPayment } from '../../../common/helpers/paymentDisplay'
import { monitoringChoiceOffered } from '../../../common/helpers/signingFlowV2'

/**
 * Signing v2, phase C (ticket 13213634281): the guided flow is split in two
 * parts, each with its own start card. Part 1 (`report`) is what the customer
 * signs on the report before the contract (Job Summary monthly payment and
 * notes); Part 2 (`contract`) is every signature inside the contract.
 */
export type SigningPart = 'report' | 'contract'
export interface SigningStep {
  field: string
  label: string
  signed: boolean
  /** Unset is read as `contract` (the only part before signing v2). */
  part?: SigningPart
}
/**
 * `v2` (signing v2 phase B, see common/helpers/signingFlowV2.ts): the Job
 * Summary payment and NOTES signatures come first (document order), M+S / CE
 * add the Financing Information signature and drop "Confirm equipment" (it
 * stays in the contract, signable by hand). Without `v2` the steps are
 * exactly the ones customers signed with before (V67).
 *
 * `securityContractShown`: the FE / HC / Wildcat / OTHER contract only
 * renders with the Security product (or no product). Without it there is no
 * customer signature to guide to, and asking for one left the card on
 * "Preparing agreement…" forever: no guided flow, as with no contract.
 */
export function signingSteps(
  company: string | undefined,
  customer: Record<string, unknown>,
  v2 = false,
  securityContractShown = true,
  roofClaimShown = false,
  /** Remote link. In person only if `AUTHORIZATION_REQUIRED_IN_PERSON`. */
  authorizationRequired = false,
): SigningStep[] {
  const contract: [string, string][] =
    // MSE, CE and ESR (13376550851) render the same M+S agreement.
    usesHomeImprovementAgreement(company)
      ? [
          ...(v2
            ? []
            : [
                [
                  'home_improvement_confirm_equipment_sign',
                  'Confirm equipment',
                ] as [string, string],
              ]),
          ['home_improvement_confirm_total_initials', 'Confirm total'],
          ...(v2
            ? [
                [
                  'home_improvement_financing_info_sign',
                  // Jaron, Oct 14: "Confirm finance terms"
                  'Confirm finance terms',
                ] as [string, string],
              ]
            : []),
          ['home_improvement_sign_owner1', 'Owner 1 signature'],
          ...(customer['home_improvement_owner2_name']
            ? [
                ['home_improvement_sign_owner2', 'Owner 2 signature'] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]
      : securityContractShown &&
        ['FE', 'HC', 'Wildcat', 'OTHER_CASH', 'OTHER_FIN'].includes(
          company || '',
        )
      ? [['security_contract_sign_customer', 'Customer signature']]
      : []
  // Only next to a contract that has guided steps: no new flow where there
  // was none (no company, unknown company, white-label tenants).
  const fields: [string, string][] =
    v2 && contract.length
      ? [
          // Oct 5 meeting: where the monitoring choice is offered the Job
          // Summary has two signatures, the Monthly Payment line and the
          // chosen option. M+S / CE keep one, on the Monthly Payment line.
          ...(monitoringChoiceOffered(company, customer)
            ? [
                ['job_summary_monthly_sign', 'Monthly payment'] as [
                  string,
                  string,
                ],
              ]
            : []),
          [
            'job_summary_payment_sign',
            monitoringChoiceOffered(company, customer)
              ? 'Monitoring option'
              : 'Monthly payment',
          ],
          // Jaron, Oct 14: the NOTES signature reads "Verify Equipment"
          ['job_summary_notes_sign', 'Verify equipment'],
          ...contract,
        ]
      : contract
  const roofFields: [string, string][] = [
    ...(roofClaimShown
      ? ([
          ['roof_claiming_client_sign', 'Roof claim — homeowner'],
          [
            'roof_claiming_company_rep_sign',
            'Roof claim — authorized representative',
          ],
        ] as [string, string][])
      : []),
    ...(authorizationRequired
      ? ([['roof_third_party_sign', 'Insurance authorization']] as [
          string,
          string,
        ][])
      : []),
  ]
  // Cash has no loan-monthly step. The independent monitoring option remains.
  const cash = isCashPayment(customer['security_loan_applied'])
  const reportFields = fields.filter(
    ([field]) =>
      !cash ||
      (field !== 'job_summary_monthly_sign' &&
        (field !== 'job_summary_payment_sign' ||
          monitoringChoiceOffered(company, customer))),
  )
  const ordered = [
    ...reportFields.filter(([field]) => stepPart(field) === 'report'),
    ...roofFields,
    ...fields.filter(([field]) => stepPart(field) === 'contract'),
  ]
  return ordered.map(([field, label]) => ({
    field,
    label,
    part: stepPart(field),
    signed:
      typeof customer[field] === 'string' && !!String(customer[field]).trim(),
  }))
}
function stepPart(field: string): SigningPart {
  return field.startsWith('job_summary_') ? 'report' : 'contract'
}
export function stepsOfPart(
  steps: SigningStep[],
  part: SigningPart,
): SigningStep[] {
  return steps.filter((s) => (s.part || 'contract') === part)
}
/** A part with no steps is complete (nothing to sign there). */
export function partComplete(steps: SigningStep[]): boolean {
  return steps.every((s) => s.signed)
}
// Part 2 cannot start until every Part 1 signature is in.
export const PART1_FIRST = 'Complete Part 1 first'
export function contractPartLocked(steps: SigningStep[]): boolean {
  return !partComplete(stepsOfPart(steps, 'report'))
}
// Ticket 13213612910: no signing (guided Start or remote link) until the rep
// picks the contract company; the customer cannot pick it in readOnly.
export const SELECT_COMPANY_FIRST = 'Select the contract company first'
export function contractCompanyMissing(company: string | undefined): boolean {
  return !(company || '').trim()
}
export function canFinish(steps: SigningStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.signed)
}
export function validSigningEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

// Pagination keeps an invisible measurement copy. Presence determines scope;
// visibility determines focus, so optional missing sections are not required.
export function renderedSigningSteps(
  steps: SigningStep[],
  root: HTMLElement | null,
): SigningStep[] {
  if (!root) return []
  return steps.filter(
    (step) =>
      step.signed ||
      !!root.querySelector('[data-guided-signature="' + step.field + '"]'),
  )
}

// Customer-facing: what each signature means, shown before and while signing.
const STEP_DESCRIPTIONS: Record<string, string> = {
  roof_claiming_client_sign:
    'Your signature accepting the roof claim agreement.',
  roof_third_party_sign:
    'Your signature authorizing the company to work with your insurance on this claim. The Policy Number on the form has to be filled in first.',
  roof_claiming_company_rep_sign:
    'The authorized company representative signs here themselves.',
  home_improvement_confirm_equipment_sign:
    'Confirms the equipment listed in the Scope of Work is what you are getting.',
  home_improvement_confirm_total_initials:
    'Your initials confirm the agreement total shown in the Scope of Work.',
  home_improvement_sign_owner1:
    'Your signature on the agreement as the property owner.',
  home_improvement_sign_owner2:
    'The second owner signs here. They must sign themselves.',
  security_contract_sign_customer: 'Your signature accepting the agreement.',
  job_summary_payment_sign:
    'Confirms the monthly payment shown in the Job Summary. If monitoring options are shown, choose one first.',
  job_summary_monthly_sign:
    'Confirms the monthly payment shown in the Job Summary.',
  job_summary_notes_sign: 'Confirms the notes listed for your job.',
  home_improvement_financing_info_sign:
    'Confirms the financing information in the agreement.',
}
export function describeStep(field: string): string {
  return STEP_DESCRIPTIONS[field] || 'Your signature on the agreement.'
}

// A different person signs these, so one adopted signature never covers them.
const SIGN_ALL_EXCLUDED = new Set([
  // Independent consent: fresh drawing, never adoption or Sign all.
  'roof_third_party_sign',
  'home_improvement_sign_owner2',
  'roof_claiming_company_rep_sign',
])
export function signAllTargets(steps: SigningStep[]): SigningStep[] {
  return steps.filter((s) => !s.signed && !SIGN_ALL_EXCLUDED.has(s.field))
}

// DocuSign-style adoption: one drawn signature and one drawn set of initials
// are placed on the following fields of the same kind with a click.
export type SignatureKind = 'signature' | 'initials'
export function signatureKind(field: string): SignatureKind {
  return /initials/.test(field) ? 'initials' : 'signature'
}
export function canAdoptInto(field: string): boolean {
  return !SIGN_ALL_EXCLUDED.has(field)
}
